import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ComparisonTable = ({ selectedCranes, onGenerateReport, onSaveComparison }) => {
  const [highlightDifferences, setHighlightDifferences] = useState(true);
  const [expandedSections, setExpandedSections] = useState({
    specifications: true,
    valuation: true,
    performance: true,
    market: false
  });

  const specificationCategories = [
    {
      id: 'specifications',
      title: 'Basic Specifications',
      rows: [
        { label: 'Make', key: 'make' },
        { label: 'Model', key: 'model' },
        { label: 'Year', key: 'year' },
        { label: 'Maximum Capacity', key: 'capacity' },
        { label: 'Operating Hours', key: 'hours', format: 'number' },
        { label: 'Boom Length', key: 'boomLength' },
        { label: 'Engine Make', key: 'engineMake' },
        { label: 'Engine Power', key: 'enginePower' },
        { label: 'Operating Weight', key: 'operatingWeight' }
      ]
    },
    {
      id: 'valuation',
      title: 'Valuation & Pricing',
      rows: [
        { label: 'Current Market Value', key: 'marketValue', format: 'currency' },
        { label: 'Value Range (Low)', key: 'valueLow', format: 'currency' },
        { label: 'Value Range (High)', key: 'valueHigh', format: 'currency' },
        { label: 'Confidence Score', key: 'confidenceScore', format: 'percentage' },
        { label: 'Days on Market', key: 'daysOnMarket', format: 'number' },
        { label: 'Depreciation Rate', key: 'depreciationRate', format: 'percentage' }
      ]
    },
    {
      id: 'performance',
      title: 'Performance Metrics',
      rows: [
        { label: 'Lift Height', key: 'liftHeight' },
        { label: 'Working Radius', key: 'workingRadius' },
        { label: 'Travel Speed', key: 'travelSpeed' },
        { label: 'Fuel Consumption', key: 'fuelConsumption' },
        { label: 'Setup Time', key: 'setupTime' },
        { label: 'Transport Weight', key: 'transportWeight' }
      ]
    },
    {
      id: 'market',
      title: 'Market Information',
      rows: [
        { label: 'Available Units', key: 'availableUnits', format: 'number' },
        { label: 'Average DOM', key: 'averageDom', format: 'number' },
        { label: 'Regional Demand', key: 'regionalDemand' },
        { label: 'Seasonal Variation', key: 'seasonalVariation', format: 'percentage' },
        { label: 'Comparable Sales', key: 'comparableSales', format: 'number' }
      ]
    }
  ];

  // Enhanced mock data for selected cranes
  const enhancedCraneData = selectedCranes?.map(crane => ({
    ...crane,
    boomLength: crane?.make === 'Liebherr' ? '84m' : crane?.make === 'Grove' ? '78m' : crane?.make === 'Terex' ? '68m' : '92m',
    engineMake: crane?.make === 'Liebherr' ? 'Liebherr D936' : crane?.make === 'Grove' ? 'Mercedes OM471' : crane?.make === 'Terex' ? 'Deutz TCD 7.8' : 'Caterpillar C18',
    enginePower: crane?.make === 'Liebherr' ? '367 kW' : crane?.make === 'Grove' ? '390 kW' : crane?.make === 'Terex' ? '300 kW' : '447 kW',
    operatingWeight: crane?.make === 'Liebherr' ? '72,000 kg' : crane?.make === 'Grove' ? '68,500 kg' : crane?.make === 'Terex' ? '58,000 kg' : '95,000 kg',
    marketValue: crane?.price,
    valueLow: crane?.make === 'Liebherr' ? '$780,000' : crane?.make === 'Grove' ? '$850,000' : crane?.make === 'Terex' ? '$720,000' : '$1,100,000',
    valueHigh: crane?.make === 'Liebherr' ? '$920,000' : crane?.make === 'Grove' ? '$990,000' : crane?.make === 'Terex' ? '$840,000' : '$1,300,000',
    confidenceScore: crane?.make === 'Liebherr' ? '92%' : crane?.make === 'Grove' ? '88%' : crane?.make === 'Terex' ? '85%' : '90%',
    daysOnMarket: crane?.make === 'Liebherr' ? 45 : crane?.make === 'Grove' ? 38 : crane?.make === 'Terex' ? 52 : 41,
    depreciationRate: crane?.make === 'Liebherr' ? '8.5%' : crane?.make === 'Grove' ? '9.2%' : crane?.make === 'Terex' ? '7.8%' : '8.1%',
    liftHeight: crane?.make === 'Liebherr' ? '84m' : crane?.make === 'Grove' ? '78m' : crane?.make === 'Terex' ? '68m' : '92m',
    workingRadius: crane?.make === 'Liebherr' ? '68m' : crane?.make === 'Grove' ? '62m' : crane?.make === 'Terex' ? '58m' : '74m',
    travelSpeed: crane?.make === 'Liebherr' ? '85 km/h' : crane?.make === 'Grove' ? '80 km/h' : crane?.make === 'Terex' ? '75 km/h' : '70 km/h',
    fuelConsumption: crane?.make === 'Liebherr' ? '28 L/h' : crane?.make === 'Grove' ? '32 L/h' : crane?.make === 'Terex' ? '26 L/h' : '35 L/h',
    setupTime: crane?.make === 'Liebherr' ? '15 min' : crane?.make === 'Grove' ? '18 min' : crane?.make === 'Terex' ? '12 min' : '22 min',
    transportWeight: crane?.make === 'Liebherr' ? '60,000 kg' : crane?.make === 'Grove' ? '58,500 kg' : crane?.make === 'Terex' ? '48,000 kg' : '75,000 kg',
    availableUnits: crane?.make === 'Liebherr' ? 12 : crane?.make === 'Grove' ? 8 : crane?.make === 'Terex' ? 15 : 6,
    averageDom: crane?.make === 'Liebherr' ? 42 : crane?.make === 'Grove' ? 35 : crane?.make === 'Terex' ? 48 : 38,
    regionalDemand: crane?.make === 'Liebherr' ? 'High' : crane?.make === 'Grove' ? 'Medium' : crane?.make === 'Terex' ? 'Medium' : 'High',
    seasonalVariation: crane?.make === 'Liebherr' ? '12%' : crane?.make === 'Grove' ? '15%' : crane?.make === 'Terex' ? '10%' : '14%',
    comparableSales: crane?.make === 'Liebherr' ? 8 : crane?.make === 'Grove' ? 6 : crane?.make === 'Terex' ? 9 : 5
  }));

  const formatValue = (value, format) => {
    if (!value) return 'N/A';
    
    switch (format) {
      case 'currency':
        return value;
      case 'number':
        return typeof value === 'number' ? value?.toLocaleString() : value;
      case 'percentage':
        return value;
      default:
        return value;
    }
  };

  const getValueDifference = (values, format) => {
    if (values?.length < 2) return false;
    
    if (format === 'currency') {
      const numericValues = values?.map(v => parseFloat(v?.replace(/[$,]/g, '')));
      const min = Math.min(...numericValues);
      const max = Math.max(...numericValues);
      return (max - min) / min > 0.1; // 10% difference threshold
    }
    
    if (format === 'number') {
      const numericValues = values?.map(v => typeof v === 'number' ? v : parseFloat(v?.toString()?.replace(/[,]/g, '')));
      const min = Math.min(...numericValues);
      const max = Math.max(...numericValues);
      return max !== min;
    }
    
    return new Set(values)?.size > 1;
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev?.[sectionId]
    }));
  };

  if (selectedCranes?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-8">
        <div className="text-center">
          <Icon name="GitCompare" size={48} className="mx-auto mb-4 text-text-secondary opacity-50" />
          <h3 className="text-lg font-medium text-text-primary mb-2">No Cranes Selected</h3>
          <p className="text-text-secondary mb-6">
            Select at least 2 cranes from the panel to start comparing specifications and valuations.
          </p>
          <div className="flex items-center justify-center space-x-4 text-sm text-text-secondary">
            <div className="flex items-center space-x-2">
              <Icon name="Search" size={16} />
              <span>Search cranes</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="Plus" size={16} />
              <span>Add to compare</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="BarChart3" size={16} />
              <span>View comparison</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-text-primary">
            Crane Comparison ({selectedCranes?.length} models)
          </h2>
          <div className="flex items-center space-x-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setHighlightDifferences(!highlightDifferences)}
              iconName={highlightDifferences ? "Eye" : "EyeOff"}
              iconPosition="left"
            >
              {highlightDifferences ? 'Hide' : 'Show'} Differences
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onSaveComparison}
              iconName="Bookmark"
              iconPosition="left"
            >
              Save Comparison
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={onGenerateReport}
              iconName="FileText"
              iconPosition="left"
            >
              Generate Report
            </Button>
          </div>
        </div>

        {/* Crane Headers */}
        <div className="grid grid-cols-1 gap-4" style={{ gridTemplateColumns: `300px repeat(${selectedCranes?.length}, 1fr)` }}>
          <div></div>
          {enhancedCraneData?.map((crane) => (
            <div key={crane?.id} className="text-center">
              <div className="w-full h-24 bg-muted rounded-lg overflow-hidden mb-3">
                <img
                  src={crane?.image}
                  alt={crane?.imageAlt}
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="font-medium text-text-primary text-sm">
                {crane?.make} {crane?.model}
              </h3>
              <p className="text-xs text-text-secondary">{crane?.year}</p>
            </div>
          ))}
        </div>
      </div>
      {/* Comparison Table */}
      <div className="overflow-x-auto">
        {specificationCategories?.map((category) => (
          <div key={category?.id} className="border-b border-border last:border-b-0">
            <button
              onClick={() => toggleSection(category?.id)}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-muted transition-smooth"
            >
              <h3 className="font-medium text-text-primary">{category?.title}</h3>
              <Icon 
                name={expandedSections?.[category?.id] ? "ChevronUp" : "ChevronDown"} 
                size={16} 
                className="text-text-secondary" 
              />
            </button>
            
            {expandedSections?.[category?.id] && (
              <div className="pb-4">
                {category?.rows?.map((row) => {
                  const values = enhancedCraneData?.map(crane => crane?.[row?.key]);
                  const hasDifference = highlightDifferences && getValueDifference(values, row?.format);
                  
                  return (
                    <div 
                      key={row?.key}
                      className={`grid grid-cols-1 gap-4 px-6 py-3 hover:bg-muted/50 transition-smooth ${
                        hasDifference ? 'bg-accent/5 border-l-2 border-l-accent' : ''
                      }`}
                      style={{ gridTemplateColumns: `300px repeat(${selectedCranes?.length}, 1fr)` }}
                    >
                      <div className="font-medium text-text-secondary text-sm flex items-center">
                        {row?.label}
                        {hasDifference && (
                          <Icon name="AlertCircle" size={12} className="ml-2 text-accent" />
                        )}
                      </div>
                      {enhancedCraneData?.map((crane) => (
                        <div key={crane?.id} className="text-sm text-text-primary text-center">
                          {formatValue(crane?.[row?.key], row?.format)}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>
      {/* Summary Footer */}
      <div className="p-6 bg-muted/30 border-t border-border">
        <div className="flex items-center justify-between">
          <div className="text-sm text-text-secondary">
            Comparison generated on {new Date()?.toLocaleDateString()} • Data confidence: 85-92%
          </div>
          <div className="flex items-center space-x-2 text-sm text-text-secondary">
            <Icon name="Shield" size={14} />
            <span>Professional valuation data</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComparisonTable;